import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Calendar as CalendarIcon, Clock, MapPin, Users, FileText, Package, Phone } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { formatDateUK, formatDateISO, formatCurrencySimple } from '../utils/formatters';
import AddressAutocomplete from '../components/AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// South African phone number validation and formatting
const validateSAPhoneNumber = (phone) => {
  const cleaned = phone.replace(/[^\d+]/g, '');
  const patterns = [
    /^\+27[0-9]{9}$/,
    /^27[0-9]{9}$/,
    /^0[0-9]{9}$/
  ];
  return patterns.some(pattern => pattern.test(cleaned));
};

const formatSAPhoneNumber = (phone) => {
  let cleaned = phone.replace(/[^\d]/g, '');
  if (cleaned.startsWith('27') && cleaned.length === 11) {
    return `+${cleaned}`;
  }
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `+27${cleaned.substring(1)}`;
  }
  if (phone.startsWith('+27')) {
    return phone.replace(/[^\d+]/g, '');
  }
  return phone;
};

// Helper function to get proper image URL
const getImageUrl = (equipment) => {
  const img = equipment?.image_url || equipment?.image;
  if (!img) return 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80';
  if (img.startsWith('/api')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('http')) return img;
  return `${BACKEND_URL}${img}`;
};

const EquipmentBookingForm = () => {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Load Google Maps for address autocomplete
  useGoogleMaps();
  
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: '',
    eventDate: location.state?.selectedDate || null,
    eventTime: '',
    duration: '',
    location: '',
    attendees: '',
    contactPhone: '',
    specialRequirements: '',
    quantity: location.state?.quantity || 1
  });

  const eventTypes = ['Wedding', 'Conference', 'Concert', 'Party', 'Corporate Event', 'Meeting', 'Festival', 'Other'];
  const durations = ['1 day', '2 days', '3 days', '4 days', '5 days', '1 week', '2 weeks'];

  const fetchEquipment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment/${equipmentId}`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
      toast({
        title: "Error",
        description: "Failed to load equipment details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (!formData.eventDate) {
        toast({
          title: "Date required",
          description: "Please select an event date",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Validate phone number
      if (!validateSAPhoneNumber(formData.contactPhone)) {
        toast({
          title: "Invalid phone number",
          description: "Please enter a valid South African phone number",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Format phone number before submission
      const formattedPhone = formatSAPhoneNumber(formData.contactPhone);

      const bookingData = {
        equipment_id: equipmentId,
        business_id: equipment.business_id,
        event_name: formData.eventName,
        event_type: formData.eventType,
        event_date: formatDateISO(formData.eventDate),
        event_time: formData.eventTime,
        duration: formData.duration,
        location: formData.location,
        attendees: parseInt(formData.attendees),
        contact_phone: formattedPhone,
        special_requirements: formData.specialRequirements || null,
        quantity: formData.quantity
      };

      const response = await axios.post(`${API}/equipment-bookings`, bookingData, {
        withCredentials: true
      });
      
      toast({
        title: "Booking Created!",
        description: "Your equipment booking has been submitted successfully",
      });
      
      navigate('/payment', { 
        state: { 
          booking: response.data,
          isEquipmentBooking: true
        } 
      });
    } catch (error) {
      console.error('Failed to create booking:', error);
      // Handle Pydantic validation errors (array of objects) or simple error messages
      let errorMessage = "Failed to create booking. Please try again.";
      const detail = error.response?.data?.detail;
      if (detail) {
        if (Array.isArray(detail)) {
          // Pydantic validation errors - extract messages
          errorMessage = detail.map(err => err.msg || err).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast({
        title: "Booking Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Equipment Not Found</h2>
          <Button onClick={() => navigate('/equipment')} className="bg-yellow-500 hover:bg-yellow-600 text-black">Back to Equipment</Button>
        </div>
      </div>
    );
  }

  const totalPrice = equipment.price * formData.quantity * (formData.duration ? parseInt(formData.duration) : 1);

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          className="mb-6 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={() => navigate(`/equipment/${equipmentId}`)}
          data-testid="back-to-equipment-detail-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Equipment Details
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Book Equipment</CardTitle>
                <CardDescription className="text-gray-400">
                  Fill in the details for your equipment rental
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Event Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center text-white">
                      <FileText className="h-4 w-4 mr-2 text-yellow-500" />
                      Event Details
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="eventName" className="text-gray-300">Event Name *</Label>
                        <Input
                          id="eventName"
                          placeholder="e.g., Company Party"
                          value={formData.eventName}
                          onChange={(e) => handleChange('eventName', e.target.value)}
                          required
                          data-testid="event-name-input"
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eventType" className="text-gray-300">Event Type *</Label>
                        <Select
                          value={formData.eventType}
                          onValueChange={(value) => handleChange('eventType', value)}
                          required
                        >
                          <SelectTrigger data-testid="event-type-select" className="bg-gray-700 border-gray-600 text-white">
                            <SelectValue placeholder="Select event type" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {eventTypes.map(type => (
                              <SelectItem key={type} value={type} className="text-gray-300 hover:text-white">{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Date, Time & Duration */}
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center text-white">
                      <CalendarIcon className="h-4 w-4 mr-2 text-yellow-500" />
                      Date &amp; Duration
                    </h3>
                    
                    <div className="space-y-2">
                      <Label className="text-gray-300">Event Date *</Label>
                      <Calendar
                        mode="single"
                        selected={formData.eventDate}
                        onSelect={(date) => handleChange('eventDate', date)}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        className="rounded-md w-full text-white"
                      />
                      {formData.eventDate && (
                        <p className="text-sm text-green-400">
                          Selected: {formatDateUK(formData.eventDate)}
                        </p>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="eventTime" className="text-gray-300">Event Time *</Label>
                        <Input
                          id="eventTime"
                          type="time"
                          value={formData.eventTime}
                          onChange={(e) => handleChange('eventTime', e.target.value)}
                          required
                          data-testid="event-time-input"
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration" className="text-gray-300">Rental Duration *</Label>
                        <Select
                          value={formData.duration}
                          onValueChange={(value) => handleChange('duration', value)}
                          required
                        >
                          <SelectTrigger data-testid="duration-select" className="bg-gray-700 border-gray-600 text-white">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {durations.map(duration => (
                              <SelectItem key={duration} value={duration} className="text-gray-300 hover:text-white">{duration}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center text-white">
                      <Package className="h-4 w-4 mr-2 text-yellow-500" />
                      Quantity
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="quantity" className="text-gray-300">Number of Units *</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        max={equipment.quantity_available}
                        value={formData.quantity}
                        onChange={(e) => handleChange('quantity', Math.min(Math.max(1, parseInt(e.target.value) || 1), equipment.quantity_available))}
                        className="w-32 bg-gray-700 border-gray-600 text-white"
                        data-testid="quantity-input"
                      />
                      <p className="text-sm text-gray-500">
                        Max available: {equipment.quantity_available}
                      </p>
                    </div>
                  </div>

                  {/* Location & Contact */}
                  <div className="space-y-4">
                    <h3 className="font-semibold flex items-center text-white">
                      <MapPin className="h-4 w-4 mr-2 text-yellow-500" />
                      Location &amp; Contact
                    </h3>
                    
                    <div className="space-y-2">
                      <AddressAutocomplete
                        label="Event Location"
                        value={formData.location}
                        onChange={(value) => handleChange('location', value)}
                        placeholder="Start typing the event address..."
                        required
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="attendees" className="text-gray-300">Expected Attendees *</Label>
                        <Input
                          id="attendees"
                          type="number"
                          placeholder="e.g., 100"
                          value={formData.attendees}
                          onChange={(e) => handleChange('attendees', e.target.value)}
                          required
                          data-testid="attendees-input"
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactPhone" className="text-gray-300 flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Contact Phone *
                        </Label>
                        <Input
                          id="contactPhone"
                          type="tel"
                          placeholder="(+27) 065 910 4999"
                          value={formData.contactPhone}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleChange('contactPhone', value);
                            if (value && !validateSAPhoneNumber(value)) {
                              setPhoneError('Please enter a valid SA number: (+27) 0XX XXX XXXX');
                            } else {
                              setPhoneError('');
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && validateSAPhoneNumber(value)) {
                              handleChange('contactPhone', formatSAPhoneNumber(value));
                            }
                          }}
                          required
                          data-testid="phone-input"
                          className={`bg-gray-700 border-gray-600 text-white ${phoneError ? 'border-red-500' : ''}`}
                        />
                        {phoneError && (
                          <p className="text-red-400 text-xs mt-1">{phoneError}</p>
                        )}
                        <p className="text-gray-500 text-xs">Format: (+27) 0XX XXX XXXX or 0XX XXX XXXX</p>
                      </div>
                    </div>
                  </div>

                  {/* Special Requirements */}
                  <div className="space-y-2">
                    <Label htmlFor="specialRequirements" className="text-gray-300">Special Requirements</Label>
                    <Textarea
                      id="specialRequirements"
                      placeholder="Any special requirements or notes..."
                      value={formData.specialRequirements}
                      onChange={(e) => handleChange('specialRequirements', e.target.value)}
                      rows={3}
                      data-testid="special-requirements-input"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  {/* Cancellation Policy Notice */}
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <p className="text-orange-400 text-sm font-medium mb-1">⚠️ Cancellation Policy</p>
                    <p className="text-orange-300/80 text-xs">
                      A <strong>20% cancellation fee</strong> applies to all paid bookings that are cancelled. 
                      Refunds (minus the fee) are processed within 5-7 business days.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                    disabled={submitting}
                    data-testid="submit-booking-btn"
                  >
                    {submitting ? 'Processing...' : 'Confirm Booking'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Booking Summary */}
          <div>
            <Card className="sticky top-24 bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <img
                    src={getImageUrl(equipment)}
                    alt={equipment.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div>
                    <h4 className="font-semibold text-white">{equipment.name}</h4>
                    <p className="text-sm text-gray-400">{equipment.category}</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Price per unit/day</span>
                    <span className="text-white">{formatCurrencySimple(equipment.price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Quantity</span>
                    <span className="text-white">{formData.quantity} unit{formData.quantity > 1 ? 's' : ''}</span>
                  </div>
                  {formData.duration && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Duration</span>
                      <span className="text-white">{formData.duration}</span>
                    </div>
                  )}
                  {formData.eventDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Date</span>
                      <span className="text-white">{formatDateUK(formData.eventDate)}</span>
                    </div>
                  )}
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex justify-between font-semibold text-lg">
                    <span className="text-white">Estimated Total</span>
                    <span className="text-yellow-500">{formatCurrencySimple(totalPrice)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Final price may vary based on rental duration
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentBookingForm;
